import { useMemo } from 'react';
import { BooleanFilterConfig, ListFilterValues } from '../components/list-filter';
import { localStore } from '../utils/storage';
import { useIsMobile } from './useIsMobile';

/**
 * Hook to parse and manage ListFilter-related URL params
 *
 * This hook encapsulates the common pattern of reading filter values from URL params
 * and updating them. The parent component remains the orchestrator (needed for API calls),
 * but this reduces boilerplate.
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useSearchParams();
 * const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
 *   categories: ['Specialty', 'RateType']
 * });
 *
 * <ListFilter
 *   values={filterValues}
 *   onFilterChange={updateFilters}
 * />
 * ```
 */
export function useListFilterParams(
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void,
  options?: {
    /**
     * List of category names to parse from URL (e.g., ['Specialty', 'RateType'])
     * Format: category_<CategoryName>=value1,value2
     */
    categories?: readonly string[];
    /**
     * localStorage key to read/write the display type preference.
     * Example: 'OrganizationConnections || ProviderConnections || OrganizationClearance'
     */
    displayTypePrefKey?: string;
    /**
     * Boolean filter configs; for each, parse queryString param and return in filterValues.booleanFilters
     */
    booleanFilters?: readonly BooleanFilterConfig[];
  }
) {
  const prefStorageKey = `prefDisplayType${options?.displayTypePrefKey}`;
  const prefOrderingKey = `prefOrdering${options?.displayTypePrefKey}`;
  // Centralize mobile detection here so pages don't need to pass it
  const isMobile = useIsMobile();
  // Parse filter values from URL params
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const filterValues = useMemo((): Partial<ListFilterValues> => {
    const search = searchParams.get('search') || '';
    const statusParam = searchParams.get('status');
    const reasonParam = searchParams.get('reason');
    const stateParam = searchParams.get('state');
    const healthcareEntity = searchParams.get('healthcare_entity') || undefined;
    // Resolve displayType with URL as the first source of truth.
    const displayTypeParam = searchParams.get('displayType');
    let displayType: 'table' | 'card' | undefined =
      displayTypeParam === 'table' || displayTypeParam === 'card'
        ? (displayTypeParam as 'table' | 'card')
        : undefined;

    // If URL has no displayType, compute the initial value as per rules:
    // 1) Mobile: always 'card'
    // 2) Desktop: prefer stored value (when available), else 'table'
    if (!displayType) {
      if (isMobile) {
        displayType = 'card';
      } else {
        if (prefStorageKey) {
          try {
            const v = localStore.get(prefStorageKey);
            if (v === 'card' || v === 'table') {
              displayType = v;
            }
          } catch {
            // ignore storage errors; fall through to default
          }
        }
        if (!displayType) displayType = 'table';
      }
    }

    // Parse filter arrays from URL params
    const statuses = statusParam ? statusParam.split(',').filter(Boolean) : [];
    const reasons = reasonParam ? reasonParam.split(',').filter(Boolean) : [];
    const states = stateParam ? stateParam.split(',').filter(Boolean) : [];

    // Parse category filters dynamically from URL params
    const categories: Record<string, string[]> = {};
    if (options?.categories) {
      options.categories.forEach((category) => {
        const paramKey = `category_${category}`;
        const paramValue = searchParams.get(paramKey);
        if (paramValue) {
          categories[category] = paramValue.split(',').filter(Boolean);
        }
      });
    }

    // Resolve ordering (single source of truth)
    // Priority: URL param -> stored pref -> default '-updated_at'
    let orderingParam = searchParams.get('ordering') || undefined;
    if (!orderingParam) {
      try {
        const stored = localStore.get(prefOrderingKey);
        if (stored) orderingParam = stored;
      } catch {
        // ignore storage errors
      }
    }
    const ordering = orderingParam || '-updated_at';

    // Parse boolean filters from URL
    const booleanFilters: Record<string, boolean> = {};
    if (options?.booleanFilters) {
      options.booleanFilters.forEach((config) => {
        const paramValue = searchParams.get(config.queryString);
        if (paramValue === 'true') {
          booleanFilters[config.queryString] = true;
        } else if (paramValue === 'false') {
          booleanFilters[config.queryString] = false;
        } else {
          booleanFilters[config.queryString] = config.defaultValue ?? true;
        }
      });
    }

    return {
      search: search || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      reasons: reasons.length > 0 ? reasons : undefined,
      states: states.length > 0 ? states : undefined,
      healthcare_entity: healthcareEntity,
      categories: Object.keys(categories).length > 0 ? categories : undefined,
      booleanFilters: Object.keys(booleanFilters).length > 0 ? booleanFilters : undefined,
      // Provide a resolved displayType so callers can rely on a single source of truth
      displayType,
      ordering,
    };
  }, [
    searchParams,
    options?.categories,
    options?.booleanFilters,
    isMobile,
    prefStorageKey,
    prefOrderingKey,
  ]);

  // Helper to update URL param for array values
  const updateArrayParam = (params: URLSearchParams, key: string, values?: string[]): void => {
    if (values && values.length > 0) {
      params.set(key, values.join(','));
    } else {
      params.delete(key);
    }
  };

  // Update filters in URL
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const updateFilters = (values: ListFilterValues) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update search
    if (values.search) {
      newSearchParams.set('search', values.search);
    } else {
      newSearchParams.delete('search');
    }

    // Update statuses, reasons, and states
    updateArrayParam(newSearchParams, 'status', values.statuses);
    updateArrayParam(newSearchParams, 'reason', values.reasons);
    updateArrayParam(newSearchParams, 'state', values.states);

    // Update healthcare_entity
    if (values.healthcare_entity) {
      newSearchParams.set('healthcare_entity', values.healthcare_entity);
    } else {
      newSearchParams.delete('healthcare_entity');
    }

    // Update category filters dynamically
    if (options?.categories) {
      // Remove all existing category params first
      options.categories.forEach((category) => {
        newSearchParams.delete(`category_${category}`);
      });

      // Set new category params from values
      if (values.categories) {
        Object.entries(values.categories).forEach(([category, categoryValues]) => {
          if (categoryValues && categoryValues.length > 0) {
            newSearchParams.set(`category_${category}`, categoryValues.join(','));
          }
        });
      }
    }

    // Update boolean filters
    if (options?.booleanFilters) {
      options.booleanFilters.forEach((config) => {
        newSearchParams.delete(config.queryString);
      });
      if (values.booleanFilters) {
        Object.entries(values.booleanFilters).forEach(([queryString, value]) => {
          newSearchParams.set(queryString, String(value));
        });
      }
    }

    // Update display type
    // Always make the URL authoritative when an explicit displayType is provided
    if (values.displayType) {
      newSearchParams.set('displayType', values.displayType);
      // Persist desktop preference when applicable
      if (!isMobile && prefStorageKey) {
        try {
          localStore.set(prefStorageKey, values.displayType);
        } catch {
          // ignore storage errors
        }
      }
    } else {
      newSearchParams.delete('displayType');
    }

    // Update ordering (persist as user preference as well)
    if (values.ordering) {
      newSearchParams.set('ordering', values.ordering);
      try {
        localStore.set(prefOrderingKey, values.ordering);
      } catch {
        // ignore storage errors
      }
    } else {
      newSearchParams.delete('ordering');
    }

    // Only update URL if params actually changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newSearchParams.toString();
    if (currentParamsString === newParamsString) {
      return;
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  return {
    filterValues,
    updateFilters,
  };
}
